/**
 * MozApiPage — admin tool for the Moz v2 Links API.
 *
 * Features
 * --------
 *   • Batch URL Metrics — up to 50 URLs per call, returns DA / PA / Spam
 *     Score / linking-domains / external-links + a rich raw payload
 *   • Site analysis tabs — Top Pages (by PA), Anchor Text distribution,
 *     Link Intersect (find domains that link to competitors but not you)
 *   • Persistent history in Mongo (`moz_lookups`) with CSV export
 *   • Settings modal for the agency-wide Access ID + Secret Key
 *   • Quota display so you can see remaining API calls
 *
 * Docs: https://moz.com/api/docs
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Search, Loader2, Trash2, Download, RefreshCw, Settings, Key, Eye, EyeOff,
  BarChart3, ShieldAlert, Link2, TrendingUp, Layers, ExternalLink, Copy,
  Plus, Radar,
} from "lucide-react";
import { toast } from "sonner";

const errText = (e, f) => e?.response?.data?.detail || e?.message || f;
const fmt = (v) => (v == null || v === "" ? "—" : String(v));
const scoreColor = (n) => {
  if (n == null) return "text-zinc-500";
  if (n >= 60) return "text-emerald-700";
  if (n >= 40) return "text-amber-700";
  if (n >= 20) return "text-orange-700";
  return "text-red-700";
};
const spamColor = (n) => {
  if (n == null) return "text-zinc-500";
  if (n <= 3) return "text-emerald-700";
  if (n <= 6) return "text-amber-700";
  return "text-red-700";
};

const MOZ_CATALOG = [
  // ── Currently wired into this page ──────────────────────────────────
  { id: "url_metrics", group: "Core metrics", available: true, wired: true,
    tab: "metrics",
    name: "URL Metrics",
    endpoint: "POST /v2/url_metrics",
    desc: "Domain Authority, Page Authority, Spam Score, linking-domain count and external-link count for a batch of up to 50 URLs / subdomains / root domains.",
    good_for: "Prospect audits, competitor tracking, monthly report tables." },
  { id: "top_pages", group: "Site analysis", available: true, wired: true,
    tab: "top",
    name: "Top Pages",
    endpoint: "POST /v2/top_pages",
    desc: "The highest Page Authority pages on a given site, sorted by PA. Returns page URL, PA, linking root domains and title.",
    good_for: "Identifying which pages on a client's site attract the most link equity." },
  { id: "anchor_text", group: "Site analysis", available: true, wired: true,
    tab: "anchor",
    name: "Anchor Text",
    endpoint: "POST /v2/anchor_text",
    desc: "Distribution of anchor text used by external links pointing at a target, with counts of external pages and root domains per phrase.",
    good_for: "Detecting over-optimised anchor patterns / manipulative link building." },
  { id: "link_intersect", group: "Backlinks", available: true, wired: true,
    tab: "intersect",
    name: "Link Intersect",
    endpoint: "POST /v2/link_intersect",
    desc: "Root domains that link to every positive target but none of the negative targets. Sorted by DA of the linking domain.",
    good_for: "Finding link-building prospects — sites that link to all your competitors but not you yet." },

  // ── Available in the v2 API but not yet wired into this UI ──────────
  { id: "links", group: "Backlinks", available: true, wired: false,
    endpoint: "POST /v2/links",
    name: "Links (inbound)",
    desc: "Full list of inbound links to a target — source URL, source PA, anchor text, nofollow flag, first-seen date. Filterable by dofollow, nofollow, redirect and recent-only.",
    good_for: "Backlink audits, disavow-file prep, spammy link detection." },
  { id: "linking_root_domains", group: "Backlinks", available: true, wired: false,
    endpoint: "POST /v2/linking_root_domains",
    name: "Linking Root Domains",
    desc: "Unique root domains that link to a target, one row per referring domain (dedupes multiple links from the same site).",
    good_for: "\"Referring domains\" chart in a monthly SEO report — the number every client cares about." },
  { id: "final_redirect", group: "URL health", available: true, wired: false,
    endpoint: "POST /v2/final_redirect",
    name: "Final Redirect",
    desc: "Follows the redirect chain from a URL and returns the final destination + HTTP code. Great for finding broken redirects or hijacked URLs.",
    good_for: "Auditing 301 chains during site migrations." },
  { id: "url_status", group: "URL health", available: true, wired: false,
    endpoint: "POST /v2/url_status",
    name: "URL Status",
    desc: "Confirms whether a URL is in Moz's index, when it was last crawled, and whether it's canonicalised elsewhere.",
    good_for: "Verifying that newly published content has been discovered by Moz's crawler." },
  { id: "link_status", group: "Backlinks", available: true, wired: false,
    endpoint: "POST /v2/link_status",
    name: "Link Status",
    desc: "For a batch of specific source→target URL pairs, confirms whether the link still exists and its current attributes (nofollow, sponsored, ugc).",
    good_for: "Monitoring paid/guest-post links you know about." },
  { id: "global_top_pages", group: "Discovery", available: true, wired: false,
    endpoint: "POST /v2/global_top_pages",
    name: "Global Top Pages",
    desc: "The highest-PA pages across Moz's entire index. Basically Moz's answer to \"most authoritative pages on the internet\".",
    good_for: "Content research, spotting emerging authorities in a niche." },
  { id: "global_top_root_domains", group: "Discovery", available: true, wired: false,
    endpoint: "POST /v2/global_top_root_domains",
    name: "Global Top Root Domains",
    desc: "The highest-DA root domains globally. A billion-row list of the most authoritative websites on the web.",
    good_for: "Building outreach shortlists filtered by DA." },
  { id: "index_metadata", group: "Meta", available: true, wired: false,
    endpoint: "GET /v2/index_metadata",
    name: "Index Metadata",
    desc: "Info about the currently live Moz Link Explorer index — build date, size, next update ETA.",
    good_for: "Timestamp footer on monthly reports (\"Moz index updated 5 days ago\")." },
  { id: "quota", group: "Meta", available: true, wired: true,
    endpoint: "GET /v2/quota/api/v2",
    name: "Quota / Usage",
    desc: "Remaining API calls per resource in your current billing period. Already displayed at the top of this page.",
    good_for: "Avoiding surprise 429s at month-end." },

  // ── Requires a different Moz product / not in the v2 Links API ─────
  { id: "mozcast", group: "Not in the Links API", available: false,
    name: "MozCast · Weather Report",
    endpoint: "— no public API —",
    desc: "MozCast is Moz's SERP-volatility index (moz.com/mozcast). It's a browser-based visualisation, not an API. There is no supported programmatic way to pull the daily volatility number.",
    good_for: "Manual reference at mozcast.com — you'd need to screenshot or scrape the page.",
    workaround: "The closest API alternatives are SEMrush Sensor, Advanced Web Rankings SERP Fluctuation, or the free Algoroo API. Happy to wire any of those in as its own module." },
  { id: "stat_rank", group: "Not in the Links API", available: false,
    name: "STAT · Keyword rank tracking",
    endpoint: "— separate product —",
    desc: "Daily keyword rank tracking (both desktop + mobile, per-location) is Moz's STAT product. It has its own API (getstat.com/api) with completely separate credentials — not the Access ID / Token used here.",
    good_for: "Client rank-tracking dashboards.",
    workaround: "If you're on the STAT plan, tell me and I'll add a separate STAT module alongside this Moz API page." },
  { id: "keyword_explorer", group: "Not in the Links API", available: false,
    name: "Keyword Explorer · Keyword Suggestions & Difficulty",
    endpoint: "— UI-only —",
    desc: "Moz's Keyword Explorer (search volume, difficulty, opportunity, SERP overview) is currently a web-app-only tool. The Keyword Metrics API existed until 2022 but was retired.",
    good_for: "Manual keyword research at moz.com/explorer.",
    workaround: "For programmatic keyword data: Google Keyword Planner via Ads API, SEMrush Keyword Overview, or Ahrefs API." },
  { id: "on_page_grader", group: "Not in the Links API", available: false,
    name: "On-Page Grader",
    endpoint: "— UI-only —",
    desc: "The A-F content optimisation score you see in Moz Pro was never exposed via API.",
    good_for: "Manual audit at moz.com/pro/on-page-grader.",
    workaround: "Woorank (already integrated on the Woorank page) or your own on-page checklist logic." },
];

const CATALOG_GROUPS = [
  "Core metrics", "Site analysis", "Backlinks",
  "URL health", "Discovery", "Meta", "Not in the Links API",
];

export default function MozApiPage() {
  const [config, setConfig] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [authMode, setAuthMode] = useState("token"); // 'token' | 'basic'
  const [apiToken, setApiToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [accessId, setAccessId] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [quota, setQuota] = useState(null);

  const [tab, setTab] = useState("metrics"); // 'metrics' | 'top' | 'anchor' | 'intersect'
  const [busy, setBusy] = useState(false);

  // URL Metrics
  const [urlText, setUrlText] = useState("");
  const [scope, setScope] = useState("url"); // 'url' | 'subdomain' | 'root_domain'
  const [note, setNote] = useState("");
  const [results, setResults] = useState([]);
  // Top pages
  const [topTarget, setTopTarget] = useState("");
  const [topResults, setTopResults] = useState([]);
  // Anchor text
  const [anchorTarget, setAnchorTarget] = useState("");
  const [anchorResults, setAnchorResults] = useState([]);
  // Link intersect
  const [intersectPos, setIntersectPos] = useState("");
  const [intersectNeg, setIntersectNeg] = useState("");
  const [intersectResults, setIntersectResults] = useState([]);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Monitored domains (persistent client-linked domains, monthly refresh)
  const [monitors, setMonitors] = useState([]);
  const [monClients, setMonClients] = useState([]);
  const [monBUs, setMonBUs] = useState([]);
  const [monClientId, setMonClientId] = useState("");
  const [monBuId, setMonBuId] = useState("");
  const [monDomain, setMonDomain] = useState("");
  const [monBusy, setMonBusy] = useState(false);
  const [monRefreshingId, setMonRefreshingId] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/moz/config");
      setConfig(data);
      setAccessId(data.access_id || "");
      // Default the dialog's tab to whichever auth flavour is currently
      // configured — new setups land on the newer Token method.
      if (data.auth_mode === "basic") setAuthMode("basic");
      else setAuthMode("token");
    } catch { /* no-op */ }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/moz/history?limit=200");
      setHistory(data.lookups || []);
    } catch (e) { toast.error(errText(e, "History failed")); }
    finally { setHistoryLoading(false); }
  }, []);

  const loadQuota = useCallback(async () => {
    try {
      const { data } = await api.get("/moz/quota");
      setQuota(data);
    } catch { setQuota(null); }
  }, []);

  const loadMonitors = useCallback(async () => {
    try {
      const { data } = await api.get("/moz/monitors");
      setMonitors(data.monitors || []);
    } catch (e) { toast.error(errText(e, "Load monitors failed")); }
  }, []);

  const loadMonClients = useCallback(async () => {
    try {
      const { data } = await api.get("/clients");
      setMonClients(data || []);
    } catch { /* no-op */ }
  }, []);

  // Business units depend on which client is selected
  useEffect(() => {
    if (!monClientId) { setMonBUs([]); setMonBuId(""); return; }
    api.get(`/clients/${monClientId}/business-units`)
      .then((r) => setMonBUs(r.data || []))
      .catch(() => setMonBUs([]));
    setMonBuId("");
  }, [monClientId]);

  useEffect(() => {
    loadConfig();
    loadHistory();
    loadMonitors();
    loadMonClients();
  }, [loadConfig, loadHistory, loadMonitors, loadMonClients]);

  useEffect(() => {
    if (config?.configured) loadQuota();
  }, [config?.configured, loadQuota]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {};
      if (authMode === "token") {
        // Explicit empty string clears the stored token
        payload.api_token = apiToken.trim();
        // Wipe any legacy Basic Auth pair when switching to token mode
        payload.access_id = "";
      } else {
        payload.access_id = accessId.trim();
        if (secretKey.trim()) payload.secret_key = secretKey.trim();
        // Wipe token when switching to Basic
        payload.api_token = "";
      }
      const { data } = await api.post("/moz/settings", payload);
      toast.success(`Moz credentials saved (${data.auth_mode})`);
      setSecretKey(""); setApiToken("");
      setShowSettings(false);
      await loadConfig();
      await loadQuota();
    } catch (e) { toast.error(errText(e, "Save failed")); }
    finally { setSavingSettings(false); }
  };

  const runUrlMetrics = async () => {
    const targets = urlText.split(/[\s,;\n\r]+/).map((s) => s.trim()).filter(Boolean);
    if (!targets.length) { toast.error("Enter one or more URLs / domains"); return; }
    if (targets.length > 50) { toast.error("Max 50 targets per batch"); return; }
    setBusy(true); setResults([]);
    try {
      const { data } = await api.post("/moz/url-metrics", {
        targets, scope, note: note.trim() || null, save: true,
      });
      setResults(data.results || []);
      toast.success(`Fetched metrics for ${data.count} target${data.count === 1 ? "" : "s"}`);
      loadHistory(); loadQuota();
    } catch (e) { toast.error(errText(e, "URL Metrics failed")); }
    finally { setBusy(false); }
  };

  const runTopPages = async () => {
    if (!topTarget.trim()) { toast.error("Enter a target domain"); return; }
    setBusy(true); setTopResults([]);
    try {
      const { data } = await api.post("/moz/top-pages", {
        target: topTarget.trim(), scope: "root_domain", limit: 25,
      });
      setTopResults(data.results || data.top_pages || []);
      loadQuota();
    } catch (e) { toast.error(errText(e, "Top pages failed")); }
    finally { setBusy(false); }
  };

  const runAnchorText = async () => {
    if (!anchorTarget.trim()) { toast.error("Enter a target domain"); return; }
    setBusy(true); setAnchorResults([]);
    try {
      const { data } = await api.post("/moz/anchor-text", {
        target: anchorTarget.trim(), scope: "root_domain", limit: 50,
      });
      setAnchorResults(data.results || data.anchors || data.anchor_text || []);
      loadQuota();
    } catch (e) { toast.error(errText(e, "Anchor text failed")); }
    finally { setBusy(false); }
  };

  const runLinkIntersect = async () => {
    const pos = intersectPos.split(/[\s,;\n\r]+/).map((s) => s.trim()).filter(Boolean);
    const neg = intersectNeg.split(/[\s,;\n\r]+/).map((s) => s.trim()).filter(Boolean);
    if (!pos.length) { toast.error("Enter at least one positive domain"); return; }
    setBusy(true); setIntersectResults([]);
    try {
      const { data } = await api.post("/moz/link-intersect", {
        positive_targets: pos, negative_targets: neg, limit: 25,
      });
      setIntersectResults(data.results || data.link_intersect || []);
      loadQuota();
    } catch (e) { toast.error(errText(e, "Link intersect failed")); }
    finally { setBusy(false); }
  };

  const deleteRow = async (lookup_id) => {
    if (!window.confirm("Delete this lookup?")) return;
    try {
      await api.delete(`/moz/history/${lookup_id}`);
      setHistory((rows) => rows.filter((r) => r.lookup_id !== lookup_id));
    } catch (e) { toast.error(errText(e, "Delete failed")); }
  };

  const clearHistory = async () => {
    if (!window.confirm(`Clear all ${history.length} entries?`)) return;
    try {
      await api.post("/moz/history/clear");
      setHistory([]); toast.success("History cleared");
    } catch (e) { toast.error(errText(e, "Clear failed")); }
  };

  // ── Monitored domain actions ──────────────────────────────────────
  const addMonitor = async () => {
    if (!monClientId) { toast.error("Pick a client"); return; }
    if (!monDomain.trim()) { toast.error("Enter a domain"); return; }
    setMonBusy(true);
    try {
      await api.post("/moz/monitors", {
        client_id: monClientId,
        business_unit_id: monBuId || null,
        domain: monDomain.trim(),
        scope: "root_domain",
        fetch_now: true,
      });
      toast.success("Monitor added — first snapshot fetched");
      setMonDomain(""); setMonBuId("");
      await loadMonitors();
      loadQuota();
    } catch (e) { toast.error(errText(e, "Add monitor failed")); }
    finally { setMonBusy(false); }
  };

  const refreshMonitor = async (mid) => {
    setMonRefreshingId(mid);
    try {
      await api.post(`/moz/monitors/${mid}/refresh`);
      toast.success("Fresh snapshot taken");
      await loadMonitors();
      loadQuota();
    } catch (e) { toast.error(errText(e, "Refresh failed")); }
    finally { setMonRefreshingId(null); }
  };

  const deleteMonitor = async (mid) => {
    if (!window.confirm("Delete this monitor and all its snapshots?")) return;
    try {
      await api.delete(`/moz/monitors/${mid}`);
      await loadMonitors();
      toast.success("Monitor removed");
    } catch (e) { toast.error(errText(e, "Delete failed")); }
  };

  const exportCsv = () => {
    const cols = ["target", "domain_authority", "page_authority", "spam_score",
      "linking_domains", "external_links", "note", "created_at"];
    const esc = (v) => v == null ? "" : /[",\n]/.test(String(v))
      ? `"${String(v).replace(/"/g, '""')}"` : String(v);
    const csv = [cols.join(",")].concat(
      history.map((r) => cols.map((c) => esc(r[c])).join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `moz-lookups-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const quotaRows = useMemo(() => {
    if (!quota) return [];
    // Moz returns different shapes across plans — normalise a couple of
    // common ones so we render something useful either way.
    const rows = [];
    if (Array.isArray(quota.quotas)) {
      for (const q of quota.quotas) {
        rows.push({
          path: q.path || q.resource || "quota",
          consumed: q.consumed ?? q.usage ?? 0,
          limit: q.limit ?? q.quota ?? "—",
        });
      }
    } else if (Array.isArray(quota.results)) {
      for (const q of quota.results) {
        rows.push({ path: q.path, consumed: q.consumed, limit: q.limit });
      }
    }
    return rows;
  }, [quota]);

  return (
    <div className="p-6 max-w-[1500px] mx-auto space-y-4" data-testid="moz-api-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500">
            <BarChart3 className="h-3.5 w-3.5" />
            <span>System · SEO intelligence</span>
          </div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-0.5">
            Moz API
          </h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            Domain Authority, Page Authority, Spam Score, backlinks and
            anchor-text data via the Moz v2 Links API. Every lookup is
            saved so you can build trend history across months.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {config && (
            <Badge className={`rounded-sm text-[10px] ${config.configured
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-amber-50 text-amber-800 border-amber-200"}`}
              data-testid="moz-config-badge"
              title={config.configured
                ? `Auth mode: ${config.auth_mode === "token" ? "API Token (recommended)" : "Legacy Basic Auth"}`
                : ""}>
              {config.configured
                ? `Connected · ${config.auth_mode === "token" ? "Token" : "Legacy"}`
                : "Not configured"}
            </Badge>
          )}
          {quotaRows.length > 0 && (
            <Badge className="rounded-sm text-[10px] bg-zinc-100 text-zinc-700 border-zinc-200"
              data-testid="moz-quota-badge">
              {quotaRows.map((q) => `${q.consumed}/${q.limit}`).slice(0, 1)}
              {" "}quota
            </Badge>
          )}
          <Button size="sm" variant="outline" onClick={() => setShowSettings(true)}
            data-testid="moz-settings-btn">
            <Settings className="h-4 w-4 mr-1.5" /> Settings
          </Button>
        </div>
      </div>

      {!config?.configured && (
        <Card className="rounded-sm border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <Key className="h-4 w-4 text-amber-700 mt-0.5" />
            <div className="text-xs text-amber-900">
              <div className="font-semibold">Moz credentials required</div>
              Grab an <strong>API Token</strong> (recommended) from
              <a href="https://moz.com/api/keys" target="_blank" rel="noreferrer"
                className="underline mx-1 inline-flex items-center gap-0.5">
                moz.com/api/keys <ExternalLink className="h-3 w-3" />
              </a>
              then click <b>Settings</b> to save it. The older Access ID +
              Secret Key pair still works if that&apos;s what your plan issued.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-200">
        {[
          ["metrics", "URL Metrics", BarChart3],
          ["top", "Top pages", TrendingUp],
          ["anchor", "Anchor text", Layers],
          ["intersect", "Link intersect", Link2],
          ["monitor", "Monitored domains", Radar],
        ].map(([k, label, Icon]) => (
          <button key={k}
            onClick={() => setTab(k)}
            className={`px-3 py-2 text-xs rounded-t-sm flex items-center gap-1.5
              ${tab === k ? "border-b-2 border-[#002FA7] text-[#002FA7] font-semibold"
                : "text-zinc-500 hover:text-zinc-900"}`}
            data-testid={`moz-tab-${k}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* URL Metrics */}
      {tab === "metrics" && (
        <Card className="rounded-sm">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label className="text-[11px]">Targets — URLs or domains, one per line (max 50)</Label>
                <textarea rows={5}
                  value={urlText}
                  onChange={(e) => setUrlText(e.target.value)}
                  className="w-full rounded-sm border border-zinc-300 px-2 py-1.5 text-xs font-mono"
                  placeholder="moz.com&#10;icebergdigital.com.au&#10;ahrefs.com/blog/on-page-seo/"
                  data-testid="moz-metrics-input" />
                <div className="text-[10px] text-zinc-500 mt-1">
                  {urlText.split(/[\s,;\n\r]+/).filter(Boolean).length} target(s) parsed
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-[11px]">Scope</Label>
                  <select value={scope} onChange={(e) => setScope(e.target.value)}
                    className="w-full mt-1 rounded-sm border border-zinc-300 px-2 py-1.5 text-xs"
                    data-testid="moz-metrics-scope">
                    <option value="url">URL (default)</option>
                    <option value="subdomain">Subdomain</option>
                    <option value="root_domain">Root domain</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[11px]">Note (optional)</Label>
                  <Input value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Prospect audit for X client"
                    data-testid="moz-metrics-note" />
                </div>
                <Button onClick={runUrlMetrics} disabled={busy || !urlText.trim()}
                  className="w-full bg-[#002FA7] hover:bg-[#001F7A] rounded-sm"
                  data-testid="moz-metrics-run">
                  {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Fetching…</>
                    : <><Search className="h-4 w-4 mr-1.5" /> Run</>}
                </Button>
              </div>
            </div>
            {results.length > 0 && (
              <div className="overflow-x-auto mt-3">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                      <th className="py-1.5 pr-3">Target</th>
                      <th className="py-1.5 pr-3">DA</th>
                      <th className="py-1.5 pr-3">PA</th>
                      <th className="py-1.5 pr-3">Spam</th>
                      <th className="py-1.5 pr-3">Linking domains</th>
                      <th className="py-1.5 pr-3">External links</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, idx) => (
                      <tr key={r.lookup_id || idx} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="py-2 pr-3 font-mono text-[11px] break-all max-w-[280px]">
                          {r.target || r.raw?.canonical_url || "—"}
                        </td>
                        <td className={`py-2 pr-3 font-bold text-lg ${scoreColor(r.domain_authority)}`}>
                          {fmt(r.domain_authority)}
                        </td>
                        <td className={`py-2 pr-3 font-bold text-lg ${scoreColor(r.page_authority)}`}>
                          {fmt(r.page_authority)}
                        </td>
                        <td className={`py-2 pr-3 font-bold ${spamColor(r.spam_score)}`}>
                          {r.spam_score != null ? `${r.spam_score}%` : "—"}
                        </td>
                        <td className="py-2 pr-3 font-mono">{fmt(r.linking_domains)}</td>
                        <td className="py-2 pr-3 font-mono">{fmt(r.external_links)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Top Pages */}
      {tab === "top" && (
        <Card className="rounded-sm">
          <CardContent className="p-4 space-y-3">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-[11px]">Target domain</Label>
                <Input value={topTarget} onChange={(e) => setTopTarget(e.target.value)}
                  placeholder="example.com"
                  className="font-mono"
                  data-testid="moz-top-input" />
              </div>
              <Button onClick={runTopPages} disabled={busy || !topTarget.trim()}
                className="bg-[#002FA7] hover:bg-[#001F7A] rounded-sm"
                data-testid="moz-top-run">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <><Search className="h-4 w-4 mr-1.5" /> Fetch top pages</>}
              </Button>
            </div>
            {topResults.length > 0 && (
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                    <th className="py-1.5 pr-3">Page</th>
                    <th className="py-1.5 pr-3">PA</th>
                    <th className="py-1.5 pr-3">Linking domains</th>
                    <th className="py-1.5">Title</th>
                  </tr>
                </thead>
                <tbody>
                  {topResults.map((r, idx) => (
                    <tr key={idx} className="border-b border-zinc-100">
                      <td className="py-2 pr-3 font-mono text-[10px] break-all max-w-[400px]">
                        {r.page || r.canonical_url}
                      </td>
                      <td className={`py-2 pr-3 font-bold ${scoreColor(r.page_authority)}`}>
                        {fmt(r.page_authority)}
                      </td>
                      <td className="py-2 pr-3 font-mono">{fmt(r.root_domains_to_page)}</td>
                      <td className="py-2 truncate max-w-[280px]" title={r.title}>{r.title}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Anchor Text */}
      {tab === "anchor" && (
        <Card className="rounded-sm">
          <CardContent className="p-4 space-y-3">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-[11px]">Target domain</Label>
                <Input value={anchorTarget} onChange={(e) => setAnchorTarget(e.target.value)}
                  placeholder="example.com"
                  className="font-mono"
                  data-testid="moz-anchor-input" />
              </div>
              <Button onClick={runAnchorText} disabled={busy || !anchorTarget.trim()}
                className="bg-[#002FA7] hover:bg-[#001F7A] rounded-sm"
                data-testid="moz-anchor-run">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <><Search className="h-4 w-4 mr-1.5" /> Fetch anchors</>}
              </Button>
            </div>
            {anchorResults.length > 0 && (
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                    <th className="py-1.5 pr-3">Anchor text</th>
                    <th className="py-1.5 pr-3">External pages</th>
                    <th className="py-1.5 pr-3">Root domains</th>
                  </tr>
                </thead>
                <tbody>
                  {anchorResults.map((r, idx) => (
                    <tr key={idx} className="border-b border-zinc-100">
                      <td className="py-2 pr-3">{r.anchor_text || r.term || "—"}</td>
                      <td className="py-2 pr-3 font-mono">{fmt(r.external_pages)}</td>
                      <td className="py-2 pr-3 font-mono">{fmt(r.external_root_domains)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Link Intersect */}
      {tab === "intersect" && (
        <Card className="rounded-sm">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px]">Positive targets (link to ALL of these)</Label>
                <textarea rows={4} value={intersectPos}
                  onChange={(e) => setIntersectPos(e.target.value)}
                  className="w-full rounded-sm border border-zinc-300 px-2 py-1.5 text-xs font-mono"
                  placeholder="competitor1.com&#10;competitor2.com"
                  data-testid="moz-intersect-pos" />
              </div>
              <div>
                <Label className="text-[11px]">Negative targets (don&apos;t already link to)</Label>
                <textarea rows={4} value={intersectNeg}
                  onChange={(e) => setIntersectNeg(e.target.value)}
                  className="w-full rounded-sm border border-zinc-300 px-2 py-1.5 text-xs font-mono"
                  placeholder="yourdomain.com"
                  data-testid="moz-intersect-neg" />
              </div>
            </div>
            <Button onClick={runLinkIntersect} disabled={busy || !intersectPos.trim()}
              className="bg-[#002FA7] hover:bg-[#001F7A] rounded-sm"
              data-testid="moz-intersect-run">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><Search className="h-4 w-4 mr-1.5" /> Find link intersect</>}
            </Button>
            {intersectResults.length > 0 && (
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                    <th className="py-1.5 pr-3">Domain</th>
                    <th className="py-1.5 pr-3">DA</th>
                    <th className="py-1.5 pr-3">Links from</th>
                  </tr>
                </thead>
                <tbody>
                  {intersectResults.map((r, idx) => (
                    <tr key={idx} className="border-b border-zinc-100">
                      <td className="py-2 pr-3 font-mono">{r.target || r.root_domain}</td>
                      <td className={`py-2 pr-3 font-bold ${scoreColor(r.domain_authority)}`}>
                        {fmt(r.domain_authority)}
                      </td>
                      <td className="py-2 pr-3 font-mono">{fmt(r.linking_targets)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Monitored domains — persistent per-client tracking, monthly refresh */}
      {tab === "monitor" && (
        <Card className="rounded-sm" data-testid="moz-monitor-tab">
          <CardContent className="p-4 space-y-4">
            <div>
              <div className="label-overline">Persistent tracking</div>
              <div className="font-display font-bold text-lg mt-0.5">
                Monitored domains
              </div>
              <p className="text-xs text-zinc-500 max-w-2xl mt-1">
                Attach a domain to a client (optionally a specific property)
                to record a DA / PA / Spam Score snapshot every month.
                Snapshots also appear on the client&apos;s <b>MOZ Data</b> tab
                with full history + CSV export.
              </p>
            </div>

            {/* Add form */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.2fr_auto] gap-2 items-end p-3 rounded-sm bg-zinc-50 border border-zinc-200">
              <div>
                <Label className="text-[11px]">Client</Label>
                <select value={monClientId}
                  onChange={(e) => setMonClientId(e.target.value)}
                  className="w-full mt-1 rounded-sm border border-zinc-300 px-2 py-1.5 text-xs"
                  data-testid="moz-monitor-client">
                  <option value="">— Select client —</option>
                  {monClients.map((c) => (
                    <option key={c.client_id} value={c.client_id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[11px]">Property (optional)</Label>
                <select value={monBuId}
                  onChange={(e) => setMonBuId(e.target.value)}
                  disabled={!monClientId}
                  className="w-full mt-1 rounded-sm border border-zinc-300 px-2 py-1.5 text-xs disabled:bg-zinc-100"
                  data-testid="moz-monitor-bu">
                  <option value="">— No property —</option>
                  {monBUs.map((b) => (
                    <option key={b.business_unit_id} value={b.business_unit_id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[11px]">Domain</Label>
                <Input value={monDomain}
                  onChange={(e) => setMonDomain(e.target.value)}
                  placeholder="www.example.com"
                  className="font-mono mt-1"
                  data-testid="moz-monitor-domain" />
              </div>
              <Button onClick={addMonitor}
                disabled={monBusy || !monClientId || !monDomain.trim()}
                className="bg-[#002FA7] hover:bg-[#001F7A] rounded-sm h-9"
                data-testid="moz-monitor-add">
                {monBusy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Adding…</>
                  : <><Plus className="h-4 w-4 mr-1.5" /> Monitor</>}
              </Button>
            </div>

            {/* List */}
            {monitors.length === 0 ? (
              <div className="text-center text-xs text-zinc-500 py-8 border border-dashed border-zinc-200 rounded-sm">
                No monitored domains yet — add one above.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                      <th className="py-1.5 pr-3">Client</th>
                      <th className="py-1.5 pr-3">Property</th>
                      <th className="py-1.5 pr-3">Domain</th>
                      <th className="py-1.5 pr-3">DA</th>
                      <th className="py-1.5 pr-3">PA</th>
                      <th className="py-1.5 pr-3">Spam</th>
                      <th className="py-1.5 pr-3">Linking</th>
                      <th className="py-1.5 pr-3">Last refresh</th>
                      <th className="py-1.5 pr-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monitors.map((m) => {
                      const clientName = monClients.find(
                        (c) => c.client_id === m.client_id)?.name
                        || m.client_id.slice(0, 10);
                      return (
                        <tr key={m.monitor_id} className="border-b border-zinc-100 hover:bg-zinc-50"
                          data-testid={`moz-monitor-row-${m.monitor_id}`}>
                          <td className="py-2 pr-3">{clientName}</td>
                          <td className="py-2 pr-3 text-zinc-500">
                            {m.business_unit_id ? m.business_unit_id.slice(0, 10) : "—"}
                          </td>
                          <td className="py-2 pr-3 font-mono">
                            {m.domain}
                            {m.last_error && (
                              <div className="text-[10px] text-red-700 mt-0.5">{m.last_error}</div>
                            )}
                          </td>
                          <td className={`py-2 pr-3 font-bold ${scoreColor(m.latest_domain_authority)}`}>
                            {fmt(m.latest_domain_authority)}
                          </td>
                          <td className={`py-2 pr-3 font-bold ${scoreColor(m.latest_page_authority)}`}>
                            {fmt(m.latest_page_authority)}
                          </td>
                          <td className={`py-2 pr-3 font-bold ${spamColor(m.latest_spam_score)}`}>
                            {m.latest_spam_score != null ? `${m.latest_spam_score}%` : "—"}
                          </td>
                          <td className="py-2 pr-3 font-mono">{fmt(m.latest_linking_domains)}</td>
                          <td className="py-2 pr-3 text-zinc-500">
                            {m.last_refresh_at
                              ? new Date(m.last_refresh_at).toLocaleDateString()
                              : "—"}
                          </td>
                          <td className="py-2 pr-3 text-right">
                            <div className="inline-flex gap-1">
                              <Button size="sm" variant="outline"
                                className="h-6 px-2 text-[10px] rounded-sm"
                                onClick={() => refreshMonitor(m.monitor_id)}
                                disabled={monRefreshingId === m.monitor_id}
                                data-testid={`moz-monitor-refresh-${m.monitor_id}`}>
                                {monRefreshingId === m.monitor_id
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : <><RefreshCw className="h-3 w-3 mr-1" /> Refresh</>}
                              </Button>
                              <Button size="sm" variant="ghost"
                                className="h-6 px-2 text-red-600"
                                onClick={() => deleteMonitor(m.monitor_id)}
                                data-testid={`moz-monitor-delete-${m.monitor_id}`}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Data catalog — what data does the Moz API actually expose? */}
      <Card className="rounded-sm" data-testid="moz-catalog">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="label-overline">Reference</div>
              <div className="font-display font-bold text-lg mt-0.5">
                Available Moz data
              </div>
              <p className="text-xs text-zinc-500 max-w-2xl mt-1">
                Every endpoint in the Moz v2 Links API, plus a straight
                answer on which Moz features don&apos;t have API access
                (MozCast, STAT, Keyword Explorer). Endpoints marked
                <b className="mx-1">Wired</b> already have a form on this
                page — the rest can be added on request.
              </p>
            </div>
            <a href="https://moz.com/api/docs" target="_blank" rel="noreferrer"
              className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1">
              Full docs <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          {CATALOG_GROUPS.map((grp) => {
            const items = MOZ_CATALOG.filter((c) => c.group === grp);
            if (!items.length) return null;
            const isUnavailable = grp === "Not in the Links API";
            return (
              <div key={grp} className="space-y-2">
                <div className={`text-[10px] uppercase tracking-widest pb-1 border-b border-zinc-200
                  ${isUnavailable ? "text-red-700" : "text-zinc-500"}`}>
                  {grp}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {items.map((c) => (
                    <div key={c.id}
                      className={`rounded-sm border p-3 space-y-1 ${
                        c.available
                          ? "border-zinc-200 bg-white"
                          : "border-red-200 bg-red-50/30"}`}
                      data-testid={`moz-catalog-${c.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                            {c.name}
                            {c.wired && (
                              <Badge className="rounded-sm text-[9px] bg-emerald-100 text-emerald-800 border-emerald-200 px-1.5 py-0">
                                Wired
                              </Badge>
                            )}
                            {c.available && !c.wired && (
                              <Badge className="rounded-sm text-[9px] bg-zinc-100 text-zinc-700 border-zinc-200 px-1.5 py-0">
                                Available · not yet in UI
                              </Badge>
                            )}
                            {!c.available && (
                              <Badge className="rounded-sm text-[9px] bg-red-100 text-red-800 border-red-200 px-1.5 py-0">
                                No API
                              </Badge>
                            )}
                          </div>
                          <div className="font-mono text-[10px] text-zinc-500 truncate">
                            {c.endpoint}
                          </div>
                        </div>
                        {c.wired && c.tab && (
                          <Button size="sm" variant="outline"
                            className="h-6 px-2 text-[10px] rounded-sm shrink-0"
                            onClick={() => setTab(c.tab)}
                            data-testid={`moz-catalog-try-${c.id}`}>
                            Try →
                          </Button>
                        )}
                      </div>
                      <p className="text-[11px] text-zinc-700 leading-relaxed">
                        {c.desc}
                      </p>
                      {c.good_for && (
                        <p className="text-[10px] text-zinc-500 italic">
                          <b>Good for:</b> {c.good_for}
                        </p>
                      )}
                      {c.workaround && (
                        <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-1.5 mt-1">
                          <b>Workaround:</b> {c.workaround}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* History */}
      <Card className="rounded-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="font-display font-bold text-sm">Lookup history</span>
              <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200 text-[10px]">
                {history.length}
              </Badge>
              {historyLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={loadHistory}
                data-testid="moz-history-refresh">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
              </Button>
              <Button size="sm" variant="outline" onClick={exportCsv}
                disabled={!history.length} data-testid="moz-history-csv">
                <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
              </Button>
              <Button size="sm" variant="outline" onClick={clearHistory}
                disabled={!history.length}
                className="text-red-700 border-red-200 hover:bg-red-50"
                data-testid="moz-history-clear">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                  <th className="py-1.5 pr-3">When</th>
                  <th className="py-1.5 pr-3">Target</th>
                  <th className="py-1.5 pr-3">DA</th>
                  <th className="py-1.5 pr-3">PA</th>
                  <th className="py-1.5 pr-3">Spam</th>
                  <th className="py-1.5 pr-3">Note</th>
                  <th className="py-1.5" />
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && !historyLoading && (
                  <tr><td colSpan={7} className="py-8 text-center text-zinc-500 italic">
                    No lookups yet — run a batch above.
                  </td></tr>
                )}
                {history.map((r) => (
                  <tr key={r.lookup_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-2 pr-3 text-zinc-500 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="py-2 pr-3 font-mono text-[10px] break-all max-w-[240px]">{r.target}</td>
                    <td className={`py-2 pr-3 font-bold ${scoreColor(r.domain_authority)}`}>
                      {fmt(r.domain_authority)}
                    </td>
                    <td className={`py-2 pr-3 font-bold ${scoreColor(r.page_authority)}`}>
                      {fmt(r.page_authority)}
                    </td>
                    <td className={`py-2 pr-3 font-bold ${spamColor(r.spam_score)}`}>
                      {r.spam_score != null ? `${r.spam_score}%` : "—"}
                    </td>
                    <td className="py-2 pr-3 italic text-zinc-500 truncate max-w-[180px]">
                      {r.note || "—"}
                    </td>
                    <td className="py-2 text-right">
                      <Button size="sm" variant="ghost"
                        className="h-6 px-1.5 text-red-600"
                        onClick={() => deleteRow(r.lookup_id)}
                        data-testid={`moz-history-delete-${r.lookup_id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Settings dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md rounded-sm" data-testid="moz-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-4 w-4 text-[#002FA7]" /> Moz API credentials
            </DialogTitle>
            <DialogDescription className="text-xs">
              Grab your credentials from{" "}
              <a className="underline" href="https://moz.com/api/keys"
                target="_blank" rel="noreferrer">moz.com/api/keys</a>. Stored
              encrypted server-side, never exposed to the browser after save.
            </DialogDescription>
          </DialogHeader>

          {/* Auth-mode toggle */}
          <div className="flex gap-1 -mx-1">
            <button type="button"
              onClick={() => setAuthMode("token")}
              className={`flex-1 rounded-sm border px-3 py-2 text-xs
                ${authMode === "token" ? "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] font-semibold"
                  : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
              data-testid="moz-settings-tab-token">
              API Token · newer
            </button>
            <button type="button"
              onClick={() => setAuthMode("basic")}
              className={`flex-1 rounded-sm border px-3 py-2 text-xs
                ${authMode === "basic" ? "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] font-semibold"
                  : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
              data-testid="moz-settings-tab-basic">
              Access ID + Secret · legacy
            </button>
          </div>

          {authMode === "token" ? (
            <div className="space-y-3">
              <div>
                <Label className="text-[11px]">
                  API Token {config?.has_api_token && (
                    <span className="ml-1 text-emerald-700 text-[10px]">· stored</span>
                  )}
                </Label>
                <div className="flex gap-2">
                  <Input type={showToken ? "text" : "password"}
                    value={apiToken}
                    onChange={(e) => setApiToken(e.target.value)}
                    placeholder={config?.has_api_token
                      ? "•••••••• (paste new token to replace, or leave blank to remove)"
                      : "Paste your API token"}
                    className="font-mono text-xs flex-1"
                    data-testid="moz-settings-api-token" />
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => setShowToken((s) => !s)}
                    data-testid="moz-settings-toggle-token">
                    {showToken ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </Button>
                </div>
                <p className="text-[10px] text-zinc-500 mt-1.5">
                  On the Moz keys page, click <b>Generate API Token</b>. The
                  token is a single long string starting with something like
                  <code className="mx-1 px-1 py-0.5 bg-zinc-100 rounded">eyJ...</code>
                  or <code className="mx-1 px-1 py-0.5 bg-zinc-100 rounded">moz_...</code>.
                  Sent to Moz as <code>x-moz-token</code> + <code>Authorization: Bearer</code>.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-[11px]">Access ID</Label>
                <Input value={accessId} onChange={(e) => setAccessId(e.target.value)}
                  className="font-mono text-xs"
                  placeholder="mozscape-1234abcd"
                  data-testid="moz-settings-access-id" />
              </div>
              <div>
                <Label className="text-[11px]">
                  Secret Key {config?.has_secret_key && (
                    <span className="ml-1 text-emerald-700 text-[10px]">· stored</span>
                  )}
                </Label>
                <div className="flex gap-2">
                  <Input type={showSecret ? "text" : "password"}
                    value={secretKey}
                    onChange={(e) => setSecretKey(e.target.value)}
                    placeholder={config?.has_secret_key ? "•••••••• (leave blank to keep)" : "Paste secret"}
                    className="font-mono text-xs flex-1"
                    data-testid="moz-settings-secret-key" />
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => setShowSecret((s) => !s)}
                    data-testid="moz-settings-toggle-secret">
                    {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </Button>
                </div>
                <p className="text-[10px] text-zinc-500 mt-1.5">
                  Legacy Mozscape credentials — sent as HTTP Basic Auth.
                  Moz recommends the newer API Token method for all new
                  integrations.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button onClick={saveSettings}
              disabled={savingSettings
                || (authMode === "token" && !apiToken.trim() && !config?.has_api_token)
                || (authMode === "basic" && !accessId.trim())}
              className="bg-[#002FA7] hover:bg-[#001F7A]"
              data-testid="moz-settings-save">
              {savingSettings ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
